/**
 * <next-htmx> Web Component
 *
 * A Shadow DOM container that isolates Bootstrap 5 (CSS + JS) from the
 * legacy Bootstrap 4 page, while keeping HTMX and Alpine.js functional
 * across the shadow boundary.
 *
 * Architecture:
 *   config.js                 ? path resolution, CSS file discovery
 *   css-loader.js             ? fetch, patch, inject stylesheets
 *   alpine-integration.js     ? Alpine init / destroy helpers
 *   htmx-integration.js       ? HTMX event wiring & content helpers
 *   bootstrap-components.js   ? tooltip, popover, accordion, modal init
 *   modal-patch.js            ? Modal._showElement monkey-patch (?? maintain!)
 *   custom-event-listeners.js ? custom event listeners
 */

import { getCSSFiles }                       from './config.js';
import { loadAndPatchCSS, injectBaseStyles } from './css-loader.js';
import { registerHtmxListeners, setContent } from './htmx-integration.js';
import { initializeAll }                     from './bootstrap-components.js';
import {registerCustomEventsListeners} from "./custom-event-listeners";

const TEMPLATE = `<div id="htmx-content-wrapper"></div>`;

class NextHtmx extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
    }

    async connectedCallback() {
        // 1. Build the shadow DOM skeleton
        this.shadowRoot.innerHTML = TEMPLATE;
        injectBaseStyles(this.shadowRoot);

        // 2. Load & patch external CSS -- await so Bootstrap styles exist before HTMX/Alpine/Bootstrap init run
        const cssFiles = getCSSFiles(this);
        try {
            await loadAndPatchCSS(this.shadowRoot, cssFiles);
        } catch (e) {
            console.error('[next-htmx] CSS load failed, continuing without external styles:', e);
        }

        // 3. Wire up HTMX + Alpine event listeners
        const wrapper = this.shadowRoot.getElementById('htmx-content-wrapper');
        if (wrapper) {
            registerHtmxListeners(this.shadowRoot, wrapper);
            registerCustomEventsListeners(this.shadowRoot, wrapper);
        }

        // 4. Bootstrap components (tooltips, modals, accordions)
        initializeAll(this.shadowRoot);

        this.forwardHtmxAttributes(wrapper);
    }

    // -- Public API ----------------------------------------------------------
    forwardHtmxAttributes(wrapper) {
        let triggerAttr = null;

        for (const attr of this.attributes) {
            if (attr.name.startsWith('data-hx-')) {
                const htmxAttr = attr.name.replace('data-', ''); // data-hx-get -> hx-get
                if (attr.name === 'data-hx-trigger') {
                    triggerAttr = attr; // save for last
                } else {
                    wrapper.setAttribute(htmxAttr, attr.value);
                }
            }
        }

        // Set trigger last so HTMX processes it after all other attributes are set
        if (triggerAttr) {
            wrapper.setAttribute('hx-trigger', triggerAttr.value);
        }
    }
    /** Replace the wrapper content and re-process HTMX + Alpine. */
    setContent(html) {
        setContent(this.shadowRoot, html);
    }

    /** Clear the wrapper. */
    clear() {
        this.setContent('');
    }
}

// Guard against re-definition: after login the authenticated page is injected via
// document.write() into the SAME window, which re-executes this bundle. A second
// customElements.define() would throw and abort the rest of app-next.js (including the
// next-htmx-bs5-ext extension registration). See html-preview.js for the same pattern.
if (!customElements.get('next-htmx')) {
    customElements.define('next-htmx', NextHtmx);
}